import React, { useEffect, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import cls from 'classnames'
import { faCheck, faTimes } from '@fortawesome/pro-regular-svg-icons'

interface IProps {
  password: string
  isPasswordFocused?: boolean
  className?: string
  onValidityChange?: CallbackWithParam<boolean>
}

function PasswordValidation({
  password,
  isPasswordFocused,
  onValidityChange,
  className
}: IProps) {
  const passwordValidation = useMemo(() => {
    if (!password.trim()) {
      return null
    }

    const validState = {
      charLength: password.length >= 8 && password.length < 255,
      containsUppercase: /[A-Z]/.test(password),
      containsLowercase: /[a-z]/.test(password),
      containsNumbers: /[0-9]/.test(password),
      containsSymbol: /[!@#$%^&*]/.test(password),
      isValid: false
    }
    validState.isValid =
      validState.charLength &&
      validState.containsUppercase &&
      validState.containsLowercase &&
      validState.containsSymbol &&
      validState.containsNumbers

    return validState
  }, [password])

  const isValid = passwordValidation?.isValid
  useEffect(() => {
    onValidityChange?.(!!isValid)
  }, [onValidityChange, isValid])

  return (
    <div
      className={cls(
        'space-y-1 rounded-md transition-all duration-300 ease-in-out overflow-hidden',
        passwordValidation?.isValid ? 'bg-green-100' : 'bg-red-100',
        isPasswordFocused || !!password.trim() ? 'h-[150px] p-3 mt-3' : 'h-0',
        className
      )}
    >
      <p className="text-xs text-gray-700 pb-1">Password requirements:</p>

      <div className="flex flex-row items-center pl-1">
        <div className="w-6 flex">
          <FontAwesomeIcon
            icon={passwordValidation?.charLength ? faCheck : faTimes}
          />
        </div>

        <p className="text-xs font-light text-gray-700">
          {`Between 8 and 255 characters`}
        </p>
      </div>

      <div className="flex flex-row items-center pl-1">
        <div className="w-6 flex">
          <FontAwesomeIcon
            icon={passwordValidation?.containsLowercase ? faCheck : faTimes}
          />
        </div>
        <p className="text-xs font-light text-gray-700">
          {`Must contain at least 1 lowercase character`}
        </p>
      </div>

      <div className="flex flex-row items-center pl-1">
        <div className="w-6 flex">
          <FontAwesomeIcon
            icon={passwordValidation?.containsUppercase ? faCheck : faTimes}
          />
        </div>
        <p className="text-xs font-light text-gray-700">
          {`Must contain at least 1 uppercase character`}
        </p>
      </div>

      <div className="flex flex-row items-center pl-1">
        <div className="w-6 flex">
          <FontAwesomeIcon
            icon={passwordValidation?.containsNumbers ? faCheck : faTimes}
          />
        </div>
        <p className="text-xs font-light text-gray-700">
          {`Must contain at least 1 number`}
        </p>
      </div>

      <div className="flex flex-row items-center pl-1">
        <div className="w-6 flex">
          <FontAwesomeIcon
            icon={passwordValidation?.containsSymbol ? faCheck : faTimes}
          />
        </div>
        <p className="text-xs font-light text-gray-700">
          {`Must contain at least 1 symbol !, @, #, $, %, ^, &, *`}
        </p>
      </div>
    </div>
  )
}

export default PasswordValidation
